import { loadScriptInHead } from '../dom';
import { parse as parseUrl } from 'url';
import {isFunction} from 'lodash';
import { stringify } from 'querystring';
import { sendAnalyticsEvent } from '../analytics';
import { REGEX_VIDEO_YOUTUBE } from '../widgetInspector';

const YOUTUBE_API_URL = 'https://www.youtube.com/iframe_api';

/*
 * REGEX_EMBED_SRC is what we use to figure out the 'src' of the iframe
 * when preparing the embed code for the Youtube API, so we should check
 * that it actually matches before doing anything.
 *
 * If 'src' exists, we should verify that it looks like a Youtube embed.
 */
const REGEX_EMBED_SRC = /src=("|')(\S+)("|')/;
export const canEnableYoutubeApi = (embedCode) =>
  REGEX_EMBED_SRC.test(embedCode)
  &&
  REGEX_VIDEO_YOUTUBE.test(embedCode);

export const prepareSrcForYoutubeApi = (src, extraQuery, location) => {
  /*
   * If the embed is a protocol-relative URL, force HTTPS. Node's 'url.parse'
   * does not understand protocol-relative URLs.
   */
  const srcToParse = src.slice(0, 2) === '//' ? `https:${src}` : src;

  const { protocol, host, pathname, query } = parseUrl(srcToParse, true);
  const newQuery = {
    ...query,
    ...extraQuery,
    enablejsapi: '1'
  };
  if (process.env.NODE_ENV === 'production') {
    newQuery.origin = `${location.protocol}//${location.hostname}`;
  }
  const search = stringify(newQuery);
  return `${protocol}//${host}${pathname}?${search}`;
};

export const prepareEmbedCodeFromExternalVideo = (
  embedCode, extraQuery = {}, location
) => {
  const src = embedCode.match(REGEX_EMBED_SRC)[2];
  return embedCode.replace(
    src,
    prepareSrcForYoutubeApi(src, extraQuery, location)
  );
};

export const generateEmbedCodeFromInternalVideo = (
  youtube_video_id, extraQuery = {}, location, meta_title = 'video'
) => {
  const src = `https://www.youtube.com/embed/${youtube_video_id}`;
  return `
    <iframe
      width="560"
      height="315"
      src=${prepareSrcForYoutubeApi(src, extraQuery, location)}
      title="${meta_title}"
      frameborder="0"
      allow="autoplay; encrypted-media"
      allowfullscreen>
    </iframe>
  `;
};

const fetchYT = () => new Promise(resolve => {
  if (window.YT) {
    return resolve(window.YT);
  }

  const existingCallback = window.onYouTubeIframeAPIReady;
  if (existingCallback) {
    window.onYouTubeIframeAPIReady = () => {
      existingCallback();
      resolve(window.YT);
    };
  } else {
    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT);
    };
  }

  loadScriptInHead('necessary', YOUTUBE_API_URL);
});

const onReady = (customEvents) => ({ target: player }) => {
  // Chartbeat Youtube tracking: https://refinery29.atlassian.net/browse/ME-322
  const _cbv = window._cbv || (window._cbv = []);
  _cbv.push(player);

  const isCued = (
    player.getPlayerState() === window.YT.PlayerState.CUED
  );
  if (isCued && isFunction(customEvents.onInitialCue)) {
    customEvents.onInitialCue(player);
  }
};

/*
 * Reference: https://developers.google.com/youtube/iframe_api_reference#onStateChange
 */
const onStateChange = (customEvents) => ({ target: player, data }) => {
  clearInterval(player.fifteenTimer);

  switch (data) {
    case window.YT.PlayerState.ENDED: {
      sendAnalyticsEvent('External Video', 'Complete', 'Youtube');
      if (isFunction(customEvents.onCompletion)) {
        customEvents.onCompletion(player);
      }
      break;
    }
    case window.YT.PlayerState.BUFFERING: {
      if (isFunction(customEvents.onBuffer)) {
        customEvents.onBuffer(player);
      }
      break;
    }
    case window.YT.PlayerState.PLAYING: {
      sendAnalyticsEvent('External Video', 'Play', 'Youtube');
      if (isFunction(customEvents.onPlay)) {
        customEvents.onPlay(player);
      }

      if (!player.reachedFifteen) {
        player.fifteenTimer = setInterval(() => {
          if (player.getCurrentTime() > 14) {
            sendAnalyticsEvent('External Video', '15 Second Mark', 'Youtube');
            player.reachedFifteen = true;
            clearInterval(player.fifteenTimer);
          }
        }, 100);
      }
      break;
    }
    case window.YT.PlayerState.PAUSED: {
      sendAnalyticsEvent('External Video', 'Pause', 'Youtube');
      break;
    }
  }
};

/*
 * Fetches YT and initializes the API on the `iframe` passed in.
 *
 * We're using this approach -- attaching the API to an already existing iframe
 * -- so that we can respect any custom playerVars on the Youtube embed that the
 * editor created.
 *
 * Documentation: https://developers.google.com/youtube/iframe_api_reference#Examples
 * (Example 1: Use API with existing <iframe>)
 *
 * Custom Events
 * -------------
 * To make the player fit our use cases more cleanly, we supply these handlers
 * instead of the YT API's native events.
 *
 * Note: all custom event handlers are called with the `player` instance by
 * convention.
 *
 * - `onInitialCue`: called when the player is ready to play -- the first time
 *   it enters the cued state. Should only ever be called once. Is never called
 *   for an autoplaying embed.
 * - `onBuffer`: called whenever the player enters the "buffering" state from any
 *   other state. This is the first event fired when the user clicks on the embed
 *   to start playing.
 * - `onPlay`: called whenever the player enters the "playing" state from any
 *   other state. Autoplaying embeds always fire this when they start playing.
 * - `onCompletion`: called when the player finishes playing. Could be called
 *   more than once, e.g., if a player finishes then the user goes back and
 *   plays the whole video again.
 *
 * You'll note that `initializeYoutubePlayer` returns a promise. There's nothing
 * in the consumers of this method that requires this, but it makes testing the
 * method easier.
 */
export const initializeYoutubePlayer = (iframe, customEvents = {}) =>
  fetchYT().then(YT => {
    new YT.Player(iframe, {
      events: {
        onReady: onReady(customEvents),
        onStateChange: onStateChange(customEvents)
      }
    });
  });
